import { useEffect, useState } from 'react';
import { Download, X, Check, MonitorDown, Smartphone } from 'lucide-react';

/**
 * Smart "Install App" button for the desktop / mobile landing header.
 *
 * Three behaviors stitched into one component:
 *
 *   1. CHROME / EDGE / SAMSUNG INTERNET - captures `beforeinstallprompt`
 *      and exposes a one-click install. Hides itself if already
 *      installed (display-mode standalone).
 *
 *   2. IOS SAFARI - there's no native install API. We detect Safari +
 *      iOS and open a small modal explaining the Share → Add to Home
 *      Screen flow.
 *
 *   3. OTHER BROWSERS - if `beforeinstallprompt` never fires AND we're
 *      not on iOS Safari, we still show the button as a passive CTA;
 *      clicking opens a generic "your browser doesn't support
 *      one-click install - try Chrome / Edge / Brave on desktop"
 *      message. This avoids dead-end "where did the button go?"
 *      confusion.
 */
export default function InstallAppButton({ variant = 'pill' }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [iosModalOpen, setIosModalOpen] = useState(false);
  const [unsupportedToast, setUnsupportedToast] = useState(false);

  useEffect(() => {
    // Already installed? Don't show the button at all.
    const standalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true; // iOS shorthand
    if (standalone) {
      setInstalled(true);
      return;
    }

    // Phase 62 - force the browser to re-fetch the new manifest +
    // service worker. The first deploy of the install button shipped
    // a manifest with mismatched icon dimensions (335x59 declared as
    // 512x512), which Chrome silently caches as "invalid manifest"
    // even after we fix it. We bump a version key in localStorage and
    // re-register the SW once per version so users pick up the fix
    // without having to clear cache manually.
    if ('serviceWorker' in navigator) {
      const PWA_VERSION = '4'; // bump when manifest name / icons change
      const stored = localStorage.getItem('byb_pwa_version');
      if (stored !== PWA_VERSION) {
        navigator.serviceWorker.getRegistrations().then((regs) => {
          regs.forEach((r) => r.update());
        }).catch(() => { /* noop */ });
        try { localStorage.setItem('byb_pwa_version', PWA_VERSION); } catch { /* noop */ }
      }
    }

    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();          // Chrome's default mini-infobar - replace with our own
      setDeferredPrompt(e);
    };
    const onAppInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onAppInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  if (installed) return null;

  const isIos =
    /iPad|iPhone|iPod/i.test(navigator.userAgent || '') &&
    !window.MSStream;
  const isSafari =
    /^((?!chrome|android).)*safari/i.test(navigator.userAgent || '');

  const handleClick = async () => {
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        const choice = await deferredPrompt.userChoice;
        if (choice.outcome === 'accepted') setInstalled(true);
      } catch {
        /* user dismissed - that's fine */
      } finally {
        setDeferredPrompt(null);
      }
      return;
    }
    if (isIos && isSafari) {
      setIosModalOpen(true);
      return;
    }
    setUnsupportedToast(true);
    setTimeout(() => setUnsupportedToast(false), 4500);
  };

  const baseButton = (
    <button
      onClick={handleClick}
      data-testid="install-app-button"
      className={
        variant === 'pill'
          ? 'inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold border transition hover:bg-orange-50'
          : 'inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition'
      }
      style={
        variant === 'pill'
          ? { background: 'white', borderColor: '#F3E8E0', color: '#C2410C' }
          : { background: '#2D3748', color: 'white', boxShadow: '0 8px 22px rgba(45,55,72,0.22)' }
      }
      title="Install Build Your Book on this device"
    >
      <Download size={16} />
      <span>Install App</span>
    </button>
  );

  return (
    <>
      {baseButton}
      {iosModalOpen && <IosInstallModal onClose={() => setIosModalOpen(false)} />}
      {unsupportedToast && <UnsupportedToast onClose={() => setUnsupportedToast(false)} />}
    </>
  );
}

function IosInstallModal({ onClose }) {
  return (
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ background: 'rgba(45, 55, 72, 0.55)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
      data-testid="install-ios-modal"
    >
      <div
        className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl border p-6"
        style={{ borderColor: '#F3E8E0', boxShadow: '0 24px 60px rgba(45,55,72,0.25)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ background: '#FFF7ED' }}>
              <Smartphone size={20} style={{ color: '#C2410C' }} />
            </div>
            <div>
              <h2 className="text-xl font-semibold" style={{ fontFamily: 'Fredoka', color: '#2D3748' }}>
                Install on iPhone / iPad
              </h2>
              <p className="text-sm mt-1" style={{ color: '#718096' }}>
                Three taps and Build Your Book lives on your home screen.
              </p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100">
            <X size={18} style={{ color: '#4A5568' }} />
          </button>
        </div>
        <ol className="space-y-3 text-sm" style={{ color: '#2D3748' }}>
          <Step n={1}>
            Tap the <strong>Share</strong> button at the bottom of Safari (the square with an up-arrow).
          </Step>
          <Step n={2}>
            Scroll down and tap <strong>Add to Home Screen</strong>.
          </Step>
          <Step n={3}>
            Tap <strong>Add</strong> in the top-right corner.
          </Step>
        </ol>
        <button
          onClick={onClose}
          className="mt-5 w-full rounded-full py-3 font-semibold"
          style={{ background: '#FF8E71', color: 'white' }}
        >
          Got it
        </button>
      </div>
    </div>
  );
}

function UnsupportedToast({ onClose }) {
  return (
    <div
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[80] max-w-md w-[calc(100%-2rem)]"
      data-testid="install-unsupported-toast"
    >
      <div
        className="rounded-2xl border bg-white px-5 py-4 flex items-start gap-3"
        style={{ borderColor: '#F3E8E0', boxShadow: '0 12px 32px rgba(45,55,72,0.18)' }}
      >
        <MonitorDown size={20} style={{ color: '#C2410C', flexShrink: 0, marginTop: 2 }} />
        <div className="flex-1">
          <p className="font-semibold text-sm" style={{ color: '#2D3748' }}>
            Install isn't supported in this browser
          </p>
          <p className="text-xs mt-1" style={{ color: '#718096' }}>
            Open Build Your Book in <strong>Chrome</strong>, <strong>Edge</strong>, or <strong>Brave</strong> on
            desktop, or <strong>Safari</strong> on iPhone, to add it as an app.
          </p>
        </div>
        <button onClick={onClose} className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-gray-100">
          <X size={14} style={{ color: '#4A5568' }} />
        </button>
      </div>
    </div>
  );
}

function Step({ n, children }) {
  return (
    <li className="flex items-start gap-3">
      <span
        className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
        style={{ background: '#FFF7ED', color: '#C2410C' }}
      >
        {n}
      </span>
      <span className="leading-relaxed" style={{ color: '#4A5568' }}>{children}</span>
    </li>
  );
}

// Tiny check-icon helper kept here to avoid pulling in another import for a
// single stylistic flourish elsewhere.
export function InstallTickBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold"
          style={{ background: '#ECFDF5', color: '#047857' }}>
      <Check size={12} /> Installed
    </span>
  );
}
